<script setup>
import downloadAndroidDark from '@images/static/download-button-android-dark.svg';
import downloadIosDark from '@images/static/download-button-ios-dark.svg';
import goodnessImage from '@images/static/goodness-testimonial.png';
import madonnaImage from '@images/static/madonna-chioma-testimonial.png';
import victorImage from '@images/static/eleojo-victor-testimonial.png';
import networkFeature from '@images/static/network-feature.png';
import jobFeature from '@images/static/job-feature.png';
import careerFeature from '@images/static/career-feature.png';
import marketplaceFeature from '@images/static/marketplace-feature.png';
import informationFeature from '@images/static/information-feature.png';

import { nextTick, ref } from 'vue';
import { ScrollAreaRoot, ScrollAreaScrollbar, ScrollAreaThumb, ScrollAreaViewport, TabsContent, TabsList, TabsRoot, TabsTrigger } from 'radix-vue';
import AppButton from '@/components/Button/Button.vue';
import AnimatedBorderFill from '@/components/Secondary/Utils/AnimatedBorder/BorderFill.vue';

const features = [
    {
        label: 'Network',
        title: 'Build Meaningful Connections',
        description: 'Corperland connects you with thousands of fellow corps members and graduates across Nigeria. Join lively channels, share your experience, gain insights, and build a professional network that supports you during and long after your NYSC service. Your next mentor, partner, or friend is just a click away.',
        image: networkFeature,
    },
    {
        label: 'Jobs',
        title: 'Land Your Dream PPA & Graduate Job',
        description: 'Discover Verified PPA and graduate jobs posted directly by our trusted partners. Every listing on the Corperland Job Board is tailored for Serving corp members, ex corpers and fresh graduates, so you never miss a relevant, high-paying opportunity.',
        image: jobFeature,
    },
    {
        label: 'Information',
        title: 'Your NYSC Questions, Answered Instantly',
        description: 'Get quick, reliable answers to all your NYSC questions — from allowances and camp life to PPA rules — directly from real corps members. Just post your query and receive helpful guidance in minutes. Fuel your NYSC experience with accurate information.',
        image: informationFeature,
    },
    {
        label: 'Marketplace',
        title: 'Your Secure NYSC Marketplace',
        description: 'Buy and sell essentials, secure housing, or offer services within our trusted community. Our secure marketplace features verified profiles and built-in messaging safeguards, providing peace of mind for every transaction. Get what you need or sell what you don\'t, safely and conveniently.',
        image: marketplaceFeature,
    },
    {
        label: 'Career',
        title: 'Build a thriving career with Corperland',
        description: 'At Corperland, we host regular, expert-led webinars on CV writing, interview skills, personal branding, and more. Get insider tips from real recruiters, ask questions live, and build the career you want—right from your phone, before or after service.',
        image: careerFeature,
    },
];

const testimonials = [
    {
        image: madonnaImage,
        content: 'Before Corperland, I was struggling to make ends meet. A friend convinced me to sign up and join the community. In just two weeks I won ₦20,000, and over time I collected more than ₦30,000 through their giveaways. Even better, my active participation led to being named Delta State Ambassador, complete with a stipend that helped when my PPA wasn’t paying me. Corperland gave me much more than cash—it connected me with corps members across Nigeria, earned me respect, and opened real opportunities. Jump in, stay active, and you might be the next success story.',
        name: 'Madonna Chioma',
        nameInfo: '2024 Batch B, Delta',
    },
    {
        image: goodnessImage,
        content: 'I joined Corperland looking for answers and found a wealth of practical tips and genuine connections. Whether it was NYSC registration details, camp life advice, PPA placement guidance or post-service career leads, someone was always ready to respond. I\'ve even discovered tech courses and skill-building resources that have helped me grow professionally. Most of all the community celebrates your milestones and encourages your progress. If you\'re starting NYSC soon or know someone who is, Corperland is the place to be.',
        name: 'Chidozie Chisom Goodness',
        nameInfo: '2024 Batch B, Akwa Ibom',
    },
    {
        image: victorImage,
        content: 'I served in Akwa Ibom with Batch C Stream II and Corperland has completely changed my service experience. Although it is still growing, it already stands out as one of the best apps for corps members. The information is always accurate and up to date, networking feels natural, and making connections is easy. Signing up for Corperland became one of the highlights of my service year. If you are serving now or about to start, join Corperland—you will be glad you did.',
        name: 'Eleojo Victor',
        nameInfo: '2023 Batch C, Akwa Ibom',
    },
];
const initialTestimonialSlide = Math.ceil(testimonials.length / 2) - 1;
const testimonialSwiper = ref();
const activeTestimonial = ref(initialTestimonialSlide);
const onSwiperActiveIndexChange = (event) => {
    const index = event.target.swiper?.activeIndex;

    if (typeof index !== 'undefined') {
        activeTestimonial.value = index;
    }
}

const activeFeature = ref(0);
const pauseFeatureAnimation = ref(false);
const stopAnimation = ref(false);

function handleTriggerClick() {
    stopAnimation.value = true;
}

function onMouseEnter() {
    pauseFeatureAnimation.value = true;
}

function onMouseLeave() {
    pauseFeatureAnimation.value = false;
    stopAnimation.value = false;
}

const playNextFeature = (currentIndex) => {
    activeFeature.value = currentIndex === features.length - 1 ? 0 : currentIndex + 1;
    nextTick(() => {
        const el = document.querySelector('[data-feature-tab][data-state="active"]');
        const parent = document.querySelector('[data-feature-tab-viewport]');
        if (el && parent) {
            parent.scrollTo({
                left: el.offsetLeft - parent.offsetLeft,
                behavior: 'smooth'
            });
        }
    })
}
</script>

<style lang="css">
body {
    overflow: auto;
}
</style>

<style lang="postcss" scoped>
.container {
    @apply px-4 md:mx-auto md:px-20 lg:px-0 lg:w-[980px] xl:w-[1200px];
}
</style>
<style lang="css" scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity .25s;
}

.fade-enter-to, .fade-leave-to {
    opacity: 1;
}
</style>

<template>
    <div>
        <!-- HERO SECTION -->
        <section class="bg-primary-light text-primary-light-foreground flex flex-col min-h-screen relative">
            <div class="container">
                <div class="flex items-center min-h-[80px] pt-6 md:pt-8">
                    <div class="">
                        <img alt="Corperland's logo" src="@images/static/logo.svg" />
                    </div>
                    <div class="mx-auto">
                    </div>

                    <app-text-button as="RouterLink" :to="{ name: 'login' }">Login</app-text-button>
                    <app-button as="RouterLink" :to="{ name: 'register' }" color="primary" capitalize compact>
                        Signup
                    </app-button>
                </div>
            </div>
            <div class="container grow flex flex-col justify-center">
                <div class="flex flex-col md:flex-row md:items-center gap-14 py-12 h-full">

                    <!-- LEFT CONTENT -->
                    <div class="max-w-xl shrink-0">
                        <h1 class="font-extrabold text-4xl md:text-5xl leading-tight mb-6">Your <span class="text-primary">NYSC</span> Journey, Connected</h1>

                        <h2 class="text-2xl sm:text-3xl font-extrabold">
                            Stop wondering. Start knowing.
                        </h2>

                        <p class="text-lg sm:text-lg font-medium mb-8">
                            Connect with Corp members nationwide, simplify your NYSC journey and find all your essentials in our <span class="text-primary font-semibold">marketplace</span>. Secure you dream PPA and discover exclusive job opportunities. Get started for free today and turn your NYSC journey into a launchpad for your future!
                        </p>
                        <div class="mb-10 w-fit">
                            <app-button lg color="primary" capitalize as="RouterLink" :to="{ name: 'register' }">
                                Get started for free
                            </app-button>
                        </div>

                        <div class="flex gap-4">
                            <a target="_blank" href="https://play.google.com/store/apps/details?id=ng.corperland.twa">
                                <span class="sr-only">Get it on google play</span>
                                <img class="h-12 w-auto" aria-label="Get it on google play" :src="downloadAndroidDark" />
                            </a>
                            <a target="_blank" href="https://apps.apple.com/ng/app/corperland/id6738648480">
                                <span class="sr-only">Download on the app store</span>
                                <img class="h-12 w-auto" aria-label="Download on the app store" :src="downloadIosDark" />
                            </a>
                        </div>
                    </div>

                    <div class="max-h-[150px] w-full md:w-[250px] relative">
                        <!-- Decorative random design with circles and squares -->
                        <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <!-- Large faded circle -->
                            <div class="rounded-full bg-primary/10 w-32 h-32 absolute -top-8 -left-8 animate-pulse"></div>
                            <!-- Small accent circle -->
                            <div class="rounded-full bg-primary/30 w-8 h-8 absolute top-4 right-8"></div>
                            <!-- Rotated square -->
                            <div class="w-10 h-10 bg-primary/20 rotate-12 absolute bottom-4 left-8"></div>
                            <!-- Tiny dot -->
                            <div class="rounded-full bg-primary/40 w-3 h-3 absolute bottom-8 right-6"></div>
                            <!-- Main central circle -->
                            <div class="rounded-full size-20 bg-primary/10"></div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- STATS -->
            <div class="bg-primary text-primary-foreground">
                <div class="container">
                    <div class="w-full flex gap-14 py-6 text-center">
                        <div>
                            <h3 class="text-2xl md:text-3xl font-bold">24/7</h3>
                            <p class="mt-2 text-xs md:text-sm max-w-32">Access to NYSC News & Resources</p>
                        </div>
                        <div>
                            <h3 class="text-2xl md:text-3xl font-bold">500+</h3>
                            <p class="mt-2 text-xs md:text-sm max-w-32">PPA & Entry Level Jobs</p>
                        </div>
                        <div>
                            <h3 class="text-2xl md:text-3xl font-bold">2K+</h3>
                            <p class="mt-2 text-xs md:text-sm max-w-32">Active User Nationwide</p>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="$store.state.deviceType !== 'mobile'" class="absolute right-0 bottom-0 lg:right-14 lg:bottom-0">
                <img class="h-[410px] lg:h-[720px] max-h-screen" loading="lazy" src="@images/static/landing-hero-image.png" />
            </div>
        </section>

        <!-- FEATURES -->
        <section class="bg-white text-white-foreground">
            <div class="container">
                <h2 class="text-4xl md:text-5xl font-bold py-10 md:py-20 text-center">How <span class="text-primary">Corperland</span> Empowers You</h2>
                <TabsRoot
                    class="flex flex-col gap-8 lg:flex-row lg:justify-between pb-24"
                    v-model="activeFeature"
                    @mouseenter="onMouseEnter"
                    @mouseleave="onMouseLeave"
                >
                    <TabsList>
                        <ScrollAreaRoot>
                            <ScrollAreaViewport data-feature-tab-viewport>
                                <div class="flex lg:flex-col gap-4">
                                    <TabsTrigger
                                        v-for="feature, index in features"
                                        :key="index"
                                        :value="index"
                                        @click="handleTriggerClick(index)"
                                        :as="AppButton"
                                        :color="index === activeFeature ? 'primary' : 'gray-border'"
                                        capitalize
                                        xl
                                        full
                                        class="relative rounded-3xl"
                                        data-feature-tab
                                    >
                                        {{ feature.label }}
                                        <AnimatedBorderFill
                                            width="6"
                                            :animate="!stopAnimation && (((index - 1) === activeFeature) || activeFeature === features.length - 1 && index === 0)"
                                            :fill="false"
                                            animate-duration="10"
                                            :pause-animation="pauseFeatureAnimation"
                                            @animation-end="playNextFeature(index - 1)"
                                            color="hsl(var(--color-primary))" />
                                    </TabsTrigger>
                                </div>
                            </ScrollAreaViewport>
                            <ScrollAreaScrollbar orientation="horizontal">
                                <ScrollAreaThumb />
                            </ScrollAreaScrollbar>
                        </ScrollAreaRoot>
                    </TabsList>

                    <TabsContent
                        v-for="feature, index in features"
                        :value="index" class="grow data-[state=active]:animate-in fade-in duration-1000">
                        <div
                            class="flex flex-col lg:flex-row md:items-center lg:justify-between gap-10"
                            :key="activeFeature"
                        >
                            <div class="w-full flex items-center justify-center">
                                <img class="w-auto h-[400px]" :src="feature.image" />
                            </div>
                            <div class="shrink-0 md:text-center lg:text-left">
                                <h3 class="md:max-w-sm text-4xl font-semibold mb-3">{{ feature.title }}</h3>
                                <p class="text-base max-w-sm">{{ feature.description }}</p>
                            </div>
                        </div>
                    </TabsContent>
                </TabsRoot>
            </div>
        </section>

        <!-- Testimonials -->
        <section class="bg-primary-light text-primary-light-foreground">
            <div class="container">
                <div class="flex flex-col lg:flex-row gap-10 py-20 md:py-32">
                    <div class="text-center lg:text-left">
                        <h2 class="text-4xl font-bold mb-4">What <span class="text-primary">Our Members</span> Are Saying</h2>
                        <p class="text-lg">Real experiences from graduates and corps members just like you.</p>
                    </div>

                    <div class="shrink-0">
                        <div class="w-full max-w-4xl mx-auto">
                            <div class="">
                                <div class="bg-white rounded-xl border border-white-foreground/10 p-6 max-w-[700px]">
                                    <p class="text-base">
                                        {{ testimonials[activeTestimonial].content }}
                                    </p>
                                    <swiper-container
                                        ref="testimonialSwiper"
                                        class="mt-4 w-[250px]"
                                        :slides-per-view="3"
                                        @swiperslidechange="onSwiperActiveIndexChange"
                                        :centered-slides="true"
                                        :slide-to-clicked-slide="true"
                                        :initial-slide="initialTestimonialSlide"
                                    >
                                        <swiper-slide v-for="testimonial, index in testimonials" class="flex flex-col gap-1 justify-center items-center">
                                            <img
                                                :src="testimonial.image"
                                                alt="Testimonial user"
                                                class="w-16 h-16 rounded-full border-4 border-white-foreground/10"
                                                :class="{
                                                    'scale-100': activeTestimonial === index,
                                                    'opacity-50 scale-75': activeTestimonial !== index
                                                }"
                                            />
                                        </swiper-slide>
                                    </swiper-container>
                                    <div v-if="testimonials[activeTestimonial]" class="flex flex-col text-center">
                                        <span class="font-semibold text-primary">{{ testimonials[activeTestimonial].name }}</span>
                                        <span class="text-xs text-gray-400">{{ testimonials[activeTestimonial].nameInfo }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- HOW IT WORKS -->
        <section class="bg-white text-white-foreground py-20">
            <div class="container">
                <h2 class="text-4xl md:text-5xl font-bold text-center mb-14">
                    Getting Started is Simple
                </h2>
                <div class="flex flex-col md:flex-row flex-wrap justify-center gap-10 md:gap-20">
                    <!-- Step 1 -->
                    <div class="flex flex-col items-center text-center max-w-xs mx-auto">
                        <div class="bg-primary/10 text-primary rounded-full flex items-center justify-center w-20 h-20 mb-6">
                            <icon-user class="size-10" />
                        </div>
                        <h3 class="text-2xl font-semibold mb-2">
                            <RouterLink :to="{ name: 'register' }">
                                Create Your Free Account
                            </RouterLink>
                        </h3>
                        <p class="text-base text-gray-500">Sign up in seconds with your email</p>
                    </div>
                    <!-- Step 2 -->
                    <div class="flex flex-col items-center text-center max-w-xs mx-auto">
                        <div class="bg-primary/10 text-primary rounded-full flex items-center justify-center w-20 h-20 mb-6">
                            <icon-connect-without-contact class="size-10" />
                        </div>
                        <h3 class="text-2xl font-semibold mb-2">
                            <RouterLink :to="{ name: 'register' }">
                                Connect and Explore
                            </RouterLink>
                        </h3>
                        <p class="text-base text-gray-500">Find fellow corps members, join groups, ask questions, and access resources.</p>
                    </div>
                    <!-- Step 3 -->
                    <div class="flex flex-col items-center text-center max-w-xs mx-auto">
                        <div class="bg-primary/10 text-primary rounded-full flex items-center justify-center w-20 h-20 mb-6">
                            <icon-clock-outline class="size-10" />
                        </div>
                        <h3 class="text-2xl font-semibold mb-2">
                            <RouterLink :to="{ name: 'register' }">
                                Benefit and Grow
                            </RouterLink>
                        </h3>
                        <p class="text-base text-gray-500">Get answers, discover opportunities, build your network, and thrive during and after NYSC.</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA -->
        <section class="bg-primary-light text-primary-light-foreground py-20">
            <div class="container flex flex-col items-center text-center relative">
                <h2 class="text-4xl md:text-5xl font-bold mb-6">
                    Ready to Join <span class="text-primary">Corperland</span>?
                </h2>
                <p class="text-lg mb-10 max-w-2xl">
                    Sign up now to connect, learn, and thrive with thousands of NYSC members nationwide. Your journey starts here!
                </p>
                <div class="flex flex-col sm:flex-row gap-4 justify-center">
                    <app-button as="RouterLink" :to="{ name: 'register' }" color="primary" capitalize xl>
                        Get Started for Free
                    </app-button>
                </div>
                <div class="flex gap-4 mt-8">
                    <a target="_blank" href="https://play.google.com/store/apps/details?id=ng.corperland.twa">
                        <span class="sr-only">Get it on google play</span>
                        <img class="h-12 w-auto" aria-label="Get it on google play" :src="downloadAndroidDark" />
                    </a>
                    <a target="_blank" href="https://apps.apple.com/ng/app/corperland/id6738648480">
                        <span class="sr-only">Download on the app store</span>
                        <img class="h-12 w-auto" aria-label="Download on the app store" :src="downloadIosDark" />
                    </a>
                </div>
                <!-- Decorative random design with circles and squares -->
                <div class="absolute top-0 left-1/2 max-w-xs inset-0 flex items-center justify-center pointer-events-none">
                    <!-- Large faded circle -->
                    <div class="rounded-full bg-primary/10 w-32 h-32 absolute -top-8 -left-8 animate-pulse"></div>
                    <!-- Small accent circle -->
                    <div class="rounded-full bg-primary/30 w-8 h-8 absolute top-4 right-8"></div>
                    <!-- Rotated square -->
                    <div class="w-10 h-10 bg-primary/20 rotate-12 absolute bottom-4 left-8"></div>
                    <!-- Tiny dot -->
                    <div class="rounded-full bg-primary/40 w-3 h-3 absolute bottom-8 right-6"></div>
                    <!-- Main central circle -->
                    <div class="rounded-full size-20 bg-primary/10"></div>
                </div>
            </div>
        </section>

        <!-- FOOTER -->
        <footer class="bg-primary text-primary-foreground py-10">
            <div class="container">
                <div class="flex flex-col md:flex-row justify-between items-center gap-6">
                    <div class="flex flex-col items-center md:items-start">
                        <img alt="Corperland's logo" src="@images/static/logo.svg" class="h-10 mb-2" />
                        <span class="text-sm text-primary-foreground/70">&copy; {{ new Date().getFullYear() }} Corperland ltd. All rights reserved.</span>
                    </div>
                    <nav class="flex flex-wrap gap-6 text-sm font-medium">
                        <RouterLink :to="{ name: 'about' }" class="hover:underline">About Us</RouterLink>
                        <RouterLink :to="{ name: 'agreements.privacy-policy'}" class="hover:underline">Privacy Policy</RouterLink>
                        <RouterLink :to="{ name: 'guidelines' }" class="hover:underline">Community Guidelines</RouterLink>
                    </nav>
                    <div class="flex gap-4">
                        <a href="https://twitter.com/corperland" target="_blank" aria-label="Twitter" class="hover:opacity-80">
                            <icon-twitter class="size-7" />
                        </a>
                        <a href="https://instagram.com/corperland" target="_blank" aria-label="Instagram" class="hover:opacity-80">
                            <icon-instagram class="size-7" />
                        </a>
                        <a href="https://facebook.com/corperland" target="_blank" aria-label="Instagram" class="hover:opacity-80">
                            <icon-facebook class="size-7" />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    </div>
</template>
